import type { ConnectGrant, ConnectGrantType, ConnectScopeResource } from "@ando/core";
import { Badge, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Lock, ShieldCheck } from "lucide-react";
import type * as React from "react";

import { GRANT_TYPE_ICON } from "./connectShared";

/**
 * SpaceScopeBanner — the TRUST CENTERPIECE of Ando Connect (June 2026).
 *
 * Ando Connect lets an owner expose a NARROW slice of their workspace to
 * guests from another company. The single most important UX job on the
 * surface is making the data boundary unmissable: the guest (and the owner
 * auditing it) must see, at a glance, EXACTLY what is interrogable — and,
 * implicitly, that nothing else is.
 *
 * Design rules applied (ui-ux skill, enforced):
 *   - Lock ICON + TEXT + an explicit chip list of what's shared. Scope is
 *     NEVER conveyed by colour alone — every chip pairs an icon with a
 *     label, and the headline pairs a lock glyph with the sentence
 *     "Gli ospiti interrogano SOLO: …".
 *   - Read-only state is shown with a dedicated badge+icon (ShieldCheck),
 *     visually + semantically distinct from a disabled control.
 *   - Calm, on-brand surface via the existing tokens (hairline border, a faint
 *     ando-orange-soft wash, cream/white chips) — no new visual language, no
 *     hardcoded hex, no loud orange fill.
 *   - Empty-grant state stays calm and explicit ("ancora niente condiviso")
 *     rather than implying open access.
 *
 * Presentation-only: it renders a NORMALIZED ``resources: {type,label}[]``
 * list. ONE banner serves every tier — the owner detail (mapped from its
 * resolved grants via ``grantsToResources``) AND the guest/chatbot (from the
 * ``GET …/scope`` endpoint's server-labeled resources) feed the SAME chips. It
 * never fetches and never counts caller-side.
 *
 * ``grants`` / ``chips`` remain as ergonomic inputs (owner detail, preview,
 * pre-counted summaries) that the banner normalizes to ``resources`` itself —
 * so callers with raw grants don't have to map them by hand.
 */

/** One scope chip: a grant TYPE with how many of it are shared. */
export interface ScopeChip {
  readonly type: ConnectGrantType;
  readonly count: number;
}

export interface SpaceScopeBannerProps {
  /**
   * NORMALIZED scope rows — the canonical input. Each row carries a
   * ``type`` (icon) + a ready-to-render ``label``. The guest/chatbot tiers
   * pass the server-labeled ``scope.resources`` here; the owner detail maps
   * its grants via ``grantsToResources``. When supplied, ``grants``/``chips``
   * are ignored.
   */
  readonly resources?: readonly ConnectScopeResource[];
  /**
   * Resolved grants (owner detail view). When supplied, resources are derived
   * by grouping per ``grant_type`` — no caller-side counting needed.
   */
  readonly grants?: readonly ConnectGrant[];
  /**
   * Pre-counted chips (list/summary, where full grant rows aren't available).
   * Ignored when ``resources`` or ``grants`` is supplied.
   */
  readonly chips?: readonly ScopeChip[];
  /**
   * Render the read-only badge. Guests are read-only by definition; the
   * owner console may pass ``false`` when previewing as owner.
   */
  readonly readOnly?: boolean;
  /** Optional override for the headline (e.g. a named guest audience). */
  readonly heading?: React.ReactNode;
  readonly className?: string;
}

function deriveChips(grants: readonly ConnectGrant[] | undefined): readonly ScopeChip[] {
  if (!grants || grants.length === 0) return [];
  const counts = new Map<ConnectGrantType, number>();
  for (const g of grants) {
    const t = g.grant_type;
    if (t === "knowledge" || t === "connector" || t === "document") {
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  // Stable, meaningful order: documents → knowledge → connectors.
  const order: ConnectGrantType[] = ["document", "knowledge", "connector"];
  return order.filter((t) => counts.has(t)).map((t) => ({ type: t, count: counts.get(t) ?? 0 }));
}

export function SpaceScopeBanner({
  resources,
  grants,
  chips,
  readOnly = true,
  heading,
  className,
}: SpaceScopeBannerProps) {
  const { i18n } = useLingui();

  // Localized label for a TYPE+COUNT chip (owner-mapped grants / pre-counted
  // summaries). The ``resources`` path supplies its own server-rendered label.
  const chipLabel = (chip: ScopeChip): string => {
    switch (chip.type) {
      case "document":
        return chip.count === 1 ? i18n._(msg`1 documento`) : i18n._(msg`${chip.count} documenti`);
      case "knowledge":
        return chip.count === 1
          ? i18n._(msg`1 base di conoscenza`)
          : i18n._(msg`${chip.count} basi di conoscenza`);
      case "connector":
        return chip.count === 1
          ? i18n._(msg`1 fonte connessa`)
          : i18n._(msg`${chip.count} fonti connesse`);
    }
  };

  // Normalize EVERY input to the same ``{type,label}`` rows — one render path.
  const rows: readonly ConnectScopeResource[] = resources
    ? resources
    : (grants ? deriveChips(grants) : (chips ?? [])).map((c) => ({
        type: c.type,
        label: chipLabel(c),
        count: c.count,
      }));
  const isEmpty = rows.length === 0;

  return (
    <section
      // Calm, on-brand trust surface — a faint warm-orange wash on cream with
      // a hairline border and the same ultra-light shadow the rest of Ando
      // uses (cf. CompanionHero / MemberSpaceView hero). Soft, not a loud
      // orange slab: the scope is carried by the lock glyph + chips, not by a
      // heavy fill.
      className={cn(
        "rounded-[14px] border border-hairline bg-ando-orange-soft/40 px-4 py-3",
        "shadow-[0_1px_2px_rgba(0,0,0,0.02)]",
        className,
      )}
      aria-label={i18n._(msg`Ambito di condivisione dello spazio`)}
    >
      <div className="flex items-start gap-3">
        {/* Lock glyph — icon half of the icon+text scope signal. A crisp
            cream chip keeps the orange glyph legible on the soft wash. */}
        <span
          aria-hidden
          className="mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-hairline bg-white/70 text-ando-orange"
        >
          <Lock size={14} strokeWidth={2} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="font-semibold text-[13px] text-ink-1">
              {heading ?? <Trans>Gli ospiti interrogano SOLO:</Trans>}
            </p>
            {/* Read-only state — badge + icon, distinct from a disabled
                control. Semantic + icon, never colour alone. */}
            {readOnly ? (
              <Badge
                variant="secondary"
                className="gap-1 py-0.5 text-[10.5px]"
                aria-label={i18n._(msg`Accesso in sola lettura`)}
              >
                <ShieldCheck size={11} strokeWidth={2.2} aria-hidden />
                <Trans>Sola lettura</Trans>
              </Badge>
            ) : null}
          </div>

          {/* The explicit chip list — the whole point of the banner. */}
          {isEmpty ? (
            <p className="mt-1.5 text-[12px] text-ink-2">
              <Trans>
                Ancora nessuna fonte condivisa. Finché non aggiungi qualcosa, gli ospiti non possono
                interrogare nulla.
              </Trans>
            </p>
          ) : (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {rows.map((row, idx) => {
                const Icon = GRANT_TYPE_ICON[row.type] ?? GRANT_TYPE_ICON.document;
                return (
                  // The same TYPE can legitimately appear twice (e.g. two
                  // distinct server-labeled resources), so key on type+index.
                  <li key={`${row.type}-${idx}`}>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border border-hairline",
                        "bg-white/70 px-2 py-1 font-medium text-[11.5px] text-ink-1",
                      )}
                    >
                      <Icon size={12} strokeWidth={2} aria-hidden />
                      {row.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
SpaceScopeBanner.displayName = "SpaceScopeBanner";
